import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Sector } from 'recharts';
import { ArrowRight, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import SEO from '@/components/seo/SEO';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';

// --- DATA ---
const registrationHistory = [{
  date: 'aug 2025',
  total: 3245000
}, {
  date: 'jul 2025',
  total: 3234000
}, {
  date: 'jun 2025',
  total: 3221000
}, {
  date: 'mei 2025',
  total: 3207000
}, {
  date: 'apr 2025',
  total: 3194000
}, {
  date: 'mrt 2025',
  total: 3177000
}, {
  date: 'feb 2025',
  total: 3159000
}, {
  date: 'jan 2025',
  total: 3143000
}, {
  date: 'dec 2024',
  total: 3128000
}, {
  date: 'nov 2024',
  total: 3113000
}, {
  date: 'okt 2024',
  total: 3095000
}, {
  date: 'sep 2024',
  total: 3072000
}];
const powerHistory = [{
  date: 'augustus 2025',
  lt5: 7033,
  bt5and15: 4682,
  gt15: 7317
}, {
  date: 'juli 2025',
  lt5: 7014,
  bt5and15: 4667,
  gt15: 7291
}, {
  date: 'juni 2025',
  lt5: 6989,
  bt5and15: 4646,
  gt15: 7258
}, {
  date: 'mei 2025',
  lt5: 6962,
  bt5and15: 4625,
  gt15: 7218
}, {
  date: 'april 2025',
  lt5: 6938,
  bt5and15: 4607,
  gt15: 7192
}, {
  date: 'maart 2025',
  lt5: 6905,
  bt5and15: 4581,
  gt15: 7159
}, {
  date: 'februari 2025',
  lt5: 6872,
  bt5and15: 4554,
  gt15: 7127
}, {
  date: 'januari 2025',
  lt5: 6841,
  bt5and15: 4531,
  gt15: 7096
}, {
  date: 'december 2024',
  lt5: 6812,
  bt5and15: 4510,
  gt15: 7066
}, {
  date: 'november 2024',
  lt5: 6783,
  bt5and15: 4488,
  gt15: 7029
}, {
  date: 'oktober 2024',
  lt5: 6746,
  bt5and15: 4461,
  gt15: 6996
}, {
  date: 'september 2024',
  lt5: 6697,
  bt5and15: 4436,
  gt15: 6956
}];
const monthlyRegistrations = [{
  month: 'Sep-24',
  registraties: 18263
}, {
  month: 'Okt-24',
  registraties: 22800
}, {
  month: 'Nov-24',
  registraties: 15500
}, {
  month: 'Dec-24',
  registraties: 16000
}, {
  month: 'Jan-25',
  registraties: 17200
}, {
  month: 'Feb-25',
  registraties: 18500
}, {
  month: 'Mrt-25',
  registraties: 17000
}, {
  month: 'Apr-25',
  registraties: 13000
}, {
  month: 'Mei-25',
  registraties: 14000
}, {
  month: 'Jun-25',
  registraties: 12000
}, {
  month: 'Jul-25',
  registraties: 13000
}, {
  month: 'Aug-25',
  registraties: 11000
}];
const StatCard = ({
  title,
  value,
  unit
}: {
  title: string;
  value: string;
  unit: string;
}) => <Card className="text-center transition-transform hover:scale-105">
        <CardContent className="p-6">
            <p className="text-muted-foreground text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-primary mt-1">
                {value} <span className="text-xl font-medium text-muted-foreground">{unit}</span>
            </p>
        </CardContent>
    </Card>;
const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  return <g>
      <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} className="font-bold text-lg">
        {payload.name}
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle} innerRadius={outerRadius + 6} outerRadius={outerRadius + 10} fill={fill} />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="hsl(var(--foreground))">{`${value.toLocaleString('nl-NL')} MW`}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="hsl(var(--muted-foreground))">
        {`(${(percent * 100).toFixed(2)}%)`}
      </text>
    </g>;
};
const DataInzichten = () => {
  const [activeTab, setActiveTab] = useState('aantal');
  const [pieIndex, setPieIndex] = useState(0);
  const onPieEnter = (_: any, index: number) => {
    setPieIndex(index);
  };
  const mapImages = {
    aantal: '/lovable-uploads/4732e082-f00f-4640-bc48-1cde6446d5c4.png',
    percentage: '/lovable-uploads/fd3f20a2-eb28-4c2c-acf3-abd7b6f7365c.png',
    vermogen: '/lovable-uploads/78dc9475-fe52-4c8c-8b08-35c145f0120b.png'
  };
  const totalRegistrations = registrationHistory[0].total.toLocaleString('nl-NL');
  const latestPower = powerHistory[0];
  const powerDistributionData = [{
    name: '<5 kW',
    value: latestPower.lt5
  }, {
    name: '5 - 15 kW',
    value: latestPower.bt5and15
  }, {
    name: '>15 kW',
    value: latestPower.gt15
  }];
  const COLORS = ['hsl(var(--primary))', 'hsl(var(--primary) / 0.7)', 'hsl(var(--primary) / 0.5)'];
  return <>
            <SEO title="Data Inzichten - Zonne-energie Registraties Nederland | Sol-Vi" description="Interactief overzicht van alle opwekinstallaties met zonnepanelen die in Nederland zijn geregistreerd. Bekijk trends, vermogens en ontwikkelingen per gemeente." />
            
            <Header />

            <main className="min-h-screen bg-background">
                <div className="container mx-auto px-6 py-20 md:py-28">
                    <section className="max-w-7xl mx-auto">
                        <div className="text-left mb-16">
                            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-indigo-600">
                                Data Inzichten Zonne-energie
                            </h1>
                            <p className="mt-4 text-lg text-muted-foreground max-w-3xl">
                                Een overzicht van alle opwekinstallaties met zonnepanelen die op energieleveren.nl zijn geregistreerd, bijgewerkt tot augustus 2025.
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-6 mb-16">
                           <StatCard title="Totaal aantal registraties" value={totalRegistrations} unit="" />
                           <StatCard title="Totaal vermogen <15 kW" value={(latestPower.lt5 + latestPower.bt5and15).toLocaleString('nl-NL')} unit="MW" />
                           <StatCard title="Totaal vermogen >15 kW" value={latestPower.gt15.toLocaleString('nl-NL')} unit="MW" />
                        </div>

                        {/* Kaart sectie */}
                        

                        {/* Grafiek sectie */}
                        <div className="mb-20 px-0">
                            <h2 className="text-2xl font-semibold mb-6">Maandelijkse ontwikkelingen</h2>
                            <div className="grid lg:grid-cols-2 gap-8">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Aantal nieuwe registraties per maand</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={monthlyRegistrations} margin={{
                      top: 5,
                      right: 20,
                      left: 10,
                      bottom: 5
                    }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={value => `${value / 1000}k`} />
                                                <Tooltip cursor={{
                        fill: 'hsl(var(--muted) / 0.5)'
                      }} formatter={value => value.toLocaleString('nl-NL')} />
                                                <Bar dataKey="registraties" name="Nieuwe registraties" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                                
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Ontwikkeling totaal aantal registraties</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={[...registrationHistory].reverse()} margin={{
                      top: 5,
                      right: 20,
                      left: 10,
                      bottom: 5
                    }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={value => `${(value / 1000000).toFixed(1)}M`} />
                                                <Tooltip formatter={value => value.toLocaleString('nl-NL')} />
                                                <Legend wrapperStyle={{
                        fontSize: "14px"
                      }} />
                                                <Line type="monotone" dataKey="total" name="Totaal registraties" stroke="hsl(var(--primary))" strokeWidth={2} dot={{
                        r: 4,
                        fill: 'hsl(var(--primary))'
                      }} activeDot={{
                        r: 8
                      }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                        
                        <Card className="mb-16">
                            <CardHeader>
                                <CardTitle className="text-2xl">Vermogensverdeling (augustus 2025)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid lg:grid-cols-5 gap-8 items-center">
                                   <div className="lg:col-span-2">
                                         <ResponsiveContainer width="100%" height={400}>
                                            <PieChart>
                                                <Pie activeIndex={pieIndex} activeShape={renderActiveShape} data={powerDistributionData} cx="50%" cy="50%" innerRadius={80} outerRadius={110} fill="hsl(var(--primary))" dataKey="value" onMouseEnter={onPieEnter}>
                                                    {powerDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                   </div>
                                   <div className="lg:col-span-3">
                                       <div className="overflow-x-auto border border-border rounded-xl">
                                            <table className="min-w-full bg-card text-sm">
                                                <thead className="bg-muted">
                                                    <tr>
                                                        <th className="p-4 text-left font-semibold text-muted-foreground">Datum</th>
                                                        <th className="p-4 text-right font-semibold text-muted-foreground">&lt;5 kW</th>
                                                        <th className="p-4 text-right font-semibold text-muted-foreground">5 - 15 kW</th>
                                                        <th className="p-4 text-right font-semibold text-muted-foreground">&gt;15 kW</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-border">
                                                    {powerHistory.slice(0, 6).map(row => <tr key={row.date} className="hover:bg-muted/50 transition-colors">
                                                            <td className="p-4 font-medium text-foreground">{row.date}</td>
                                                            <td className="p-4 text-right text-muted-foreground">{row.lt5.toLocaleString('nl-NL')}</td>
                                                            <td className="p-4 text-right text-muted-foreground">{row.bt5and15.toLocaleString('nl-NL')}</td>
                                                            <td className="p-4 text-right text-muted-foreground">{row.gt15.toLocaleString('nl-NL')}</td>
                                                        </tr>)}
                                                </tbody>
                                            </table>
                                        </div>
                                   </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Over data sectie */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-2xl">Over deze data</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4 text-muted-foreground">
                                <p>De gegevens op deze pagina omvatten alle registraties gedaan voor 1 augustus 2025. De registratie van een installatie kan later plaatsvinden dan het installeren zelf, waardoor cijfers kunnen achterlopen op de werkelijkheid. Daarnaast kunnen er registraties missen. Het gerapporteerde vermogen is het AC- of uitgangsvermogen van de omvormer in kilowatt. Het wattpiekvermogen ligt gemiddeld 5% hoger.</p>
                                <div>
                                     <Button variant="outline" className="inline-flex items-center" asChild>
                                       <a href="https://prd.content-energieleveren-nl.pages.dev/data/open-data-energieleveren.csv" download target="_blank" rel="noopener noreferrer">
                                         Download gedetailleerde data (Open data Energieleveren.nl)
                                         <Download className="h-4 w-4 ml-2" />
                                       </a>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </main>

            <Footer />
        </>;
};
export default DataInzichten;